/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/**
 * Pure, Firebase/Sentry-decoupled instrumentation for the Walk Me Home flow's
 * previously-SILENT failure points (Phase-0 silent-failure audit). Covers BOTH
 * WMH implementations — System A (`WalkMeHomeContext`, RTDB `walkMeHome/*`,
 * SMS-backed) and System B (`useWalkMeHome` + `useDeviationDetection`, RTDB
 * `activeWalkMeHome/*`, deviation→SOS).
 *
 * Each helper takes a `WmhInstrumentationSink` (the real Sentry adapter in the
 * hooks/context; a mock in tests, per the CLAUDE.md DI pattern — mirrors
 * `sosEventWrite.ts` / `sosInstrumentation.ts`). Call sites invoke these so the
 * "which failure → which capture, tags, context" decisions are unit-testable
 * with no Firebase/React plumbing.
 *
 * PRIVACY: message strings are static step identifiers. Phone numbers and GPS
 * coordinates MUST NEVER be passed in — only session ids, counts, watcher
 * counts, the WMH `system` ("A" | "B"), and error codes go into structured
 * tags/extra. `describeError` (from sosEventWrite) extracts code/message/stack
 * without JSON.stringify, which renders Firebase errors as `{}`.
 */

import { describeError, type SosBreadcrumb } from "./sosEventWrite";

type CaptureContext = {
  level?: "info" | "warning" | "error";
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};

export interface WmhInstrumentationSink {
  addBreadcrumb: (b: SosBreadcrumb) => void;
  captureMessage: (message: string, ctx: CaptureContext) => void;
  captureException: (err: unknown, ctx: CaptureContext) => void;
}

/** Which WMH implementation the failure came from. */
export type WmhSystem = "A" | "B";

const AREA = "walk-me-home";

/**
 * The location watch's error callback fired — GPS/permission failure. Both
 * systems previously passed an empty `() => {}` here, so a walk could look
 * active while NO location was ever written (invisible to watchers). This is
 * the P0 silent point. Captured with the error code (never coordinates).
 */
export function recordLocationWatchError(
  sink: WmhInstrumentationSink,
  err: unknown,
  ctx: { system: WmhSystem; sessionId?: string },
): void {
  const info = describeError(err);
  sink.captureException(err, {
    tags: { area: AREA, system: ctx.system, phase: "location_watch" },
    extra: {
      sessionId: ctx.sessionId,
      code: info.code,
      message: info.message,
    },
  });
}

/**
 * An RTDB write on the WMH monitoring/lifecycle path rejected. Previously
 * console-only (`console.warn`) or an empty `catch {}`. `phase` names the write
 * (e.g. "session_init", "location_push", "status_update", "session_remove",
 * "overdue_flag", "update_location"). Captured so a failing safety write is
 * visible. Never throws — the walk must keep running.
 */
export function recordRtdbWriteFailed(
  sink: WmhInstrumentationSink,
  err: unknown,
  ctx: { system: WmhSystem; phase: string; sessionId?: string },
): void {
  const info = describeError(err);
  sink.captureException(err, {
    tags: { area: AREA, system: ctx.system, phase: ctx.phase },
    extra: {
      sessionId: ctx.sessionId,
      code: info.code,
      message: info.message,
      stack: info.stack,
    },
  });
}

/**
 * The deviation fan-out to watchers failed (System B). Called via `void` at
 * every site, so a rejection was silently dropped — a real off-route deviation
 * would never reach watchers, with zero trace. Safety-critical. Captured.
 */
export function recordDeviationFanoutFailed(
  sink: WmhInstrumentationSink,
  err: unknown,
  ctx: { sessionId?: string; deviationType?: string; severity?: string },
): void {
  const info = describeError(err);
  sink.captureException(err, {
    tags: { area: AREA, system: "B", phase: "deviation_fanout" },
    extra: {
      sessionId: ctx.sessionId,
      deviationType: ctx.deviationType,
      severity: ctx.severity,
      code: info.code,
      message: info.message,
    },
  });
}

/**
 * The automated SOS escalation failed (System B). Called via `void` from the
 * deviation ladder, so a rejection was silently dropped — the automated SOS
 * never fired and nobody was notified. Highest-severity silent point. Captured.
 */
export function recordEscalateFailed(
  sink: WmhInstrumentationSink,
  err: unknown,
  ctx: { sessionId?: string },
): void {
  const info = describeError(err);
  sink.captureException(err, {
    tags: { area: AREA, system: "B", phase: "escalate_to_sos" },
    extra: {
      sessionId: ctx.sessionId,
      code: info.code,
      message: info.message,
      stack: info.stack,
    },
  });
}

/**
 * A partial escalation: escalateToSOS ran but `activeSession` was null, so NO
 * SOS event was created and NO watchers were notified — only the session
 * status flipped. Looks like success to the caller. Captured as a distinct
 * warning so this silent partial failure is visible.
 */
export function recordEscalateNoSession(
  sink: WmhInstrumentationSink,
  sessionId: string,
): void {
  sink.captureMessage("wmh.escalate_no_active_session", {
    level: "error",
    tags: { area: AREA, system: "B", phase: "escalate_to_sos" },
    extra: { sessionId },
  });
}

/**
 * A walk started with zero watchers, so the start-walk SMS safety net was
 * skipped entirely. Breadcrumb (not captured): may be an intentional solo
 * walk, but the trail matters if a later failure is investigated.
 */
export function recordStartedWithoutWatchers(
  sink: WmhInstrumentationSink,
  sessionId: string,
): void {
  sink.addBreadcrumb({
    category: AREA,
    level: "warning",
    message: "wmh.started_without_watchers",
    data: { sessionId },
  });
}
